// EditableShape adapter for GroundShape objects from GardenObjectState
// Mirrors ZoneAdapter — same center + center-relative coordinate convention.
// x,y = CENTER, points = CENTER-RELATIVE (converted from API on import)

import type { EditableShape } from '@/types/editor'
import type { GardenObject } from '@/types/garden'
import { gardenObjectState } from '@/state/GardenObjectState'
import { generatePathPolygon } from '@/utils/pathGeometry'
import { undoStack } from '../UndoStack'
import { debug } from '@/utils/debug'

export class GroundShapeAdapter implements EditableShape {
  readonly sourceType = 'groundShape' as const
  private objectId: string

  /** Snapshot taken at beginEdit(), used to compute undo delta */
  private snapshot: {
    x: number; y: number; width: number; height: number; rotation: number
    points?: Array<{ x: number; y: number }>
    pathWaypoints?: Array<{ x: number; y: number }>
    pathWidth?: number
  } | null = null

  constructor(obj: GardenObject) {
    this.objectId = obj.id
  }

  get id(): string {
    return this.objectId
  }

  private getObject(): GardenObject {
    const obj = gardenObjectState.getObjectById(this.objectId)
    if (!obj) throw new Error(`GroundShapeAdapter: object ${this.objectId} not found`)
    return obj
  }

  // --- Read geometry (editor coords, dots) ---
  // x,y = CENTER, points = CENTER-RELATIVE (same as ZoneAdapter)

  getCenter(): { x: number; y: number } {
    const o = this.getObject()
    return { x: o.x, y: o.y }
  }

  getSize(): { width: number; height: number } {
    const o = this.getObject()
    return { width: o.width, height: o.height }
  }

  getRotation(): number {
    return this.getObject().rotation
  }

  getShapeKind(): 'rectangle' | 'ellipse' | 'polygon' {
    const o = this.getObject()
    if (o.type === 'ellipse') return 'ellipse'
    if (o.type === 'polygon' || o.type === 'path') return 'polygon'
    return 'rectangle'
  }

  getPoints(): Array<{ x: number; y: number }> | null {
    const o = this.getObject()
    // For path shapes, edit the waypoints (not the outline)
    if (o.type === 'path' && o.pathWaypoints) return o.pathWaypoints
    return o.points ?? null
  }

  isOpenPath(): boolean {
    return this.getObject().type === 'path'
  }

  // --- Write geometry (editor coords, dots) ---

  moveTo(x: number, y: number): void {
    gardenObjectState.updateObject(this.objectId, { x, y })
  }

  resize(width: number, height: number): void {
    gardenObjectState.updateObject(this.objectId, { width, height })
  }

  rotate(degrees: number): void {
    gardenObjectState.updateObject(this.objectId, { rotation: degrees })
  }

  setPoints(points: Array<{ x: number; y: number }>): void {
    const o = this.getObject()
    if (o.type === 'path' && o.pathWidth) {
      // Regenerate outline from updated waypoints
      const outline = generatePathPolygon(points, o.pathWidth)
      const xs = outline.map(p => p.x)
      const ys = outline.map(p => p.y)
      gardenObjectState.updateObject(this.objectId, {
        pathWaypoints: points,
        points: outline,
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      })
      return
    }
    gardenObjectState.updateObject(this.objectId, { points })
  }

  // --- Visual ---

  getAccentColor(): number {
    return 0xf76d33 // Ground layer orange
  }

  // --- Undo integration ---

  beginEdit(): void {
    const o = this.getObject()
    this.snapshot = {
      x: o.x, y: o.y, width: o.width, height: o.height, rotation: o.rotation,
      points: o.points ? o.points.map(p => ({ ...p })) : undefined,
      pathWaypoints: o.pathWaypoints ? o.pathWaypoints.map(p => ({ ...p })) : undefined,
      pathWidth: o.pathWidth,
    }
  }

  commit(): void {
    if (!this.snapshot) return

    const before = this.snapshot
    const o = this.getObject()
    const after = {
      x: o.x, y: o.y, width: o.width, height: o.height, rotation: o.rotation,
      points: o.points ? o.points.map(p => ({ ...p })) : undefined,
      pathWaypoints: o.pathWaypoints ? o.pathWaypoints.map(p => ({ ...p })) : undefined,
      pathWidth: o.pathWidth,
    }

    // Skip if nothing changed
    const pointsChanged = JSON.stringify(before.points) !== JSON.stringify(after.points)
    if (before.x === after.x && before.y === after.y &&
        before.width === after.width && before.height === after.height &&
        before.rotation === after.rotation && !pointsChanged) {
      this.snapshot = null
      return
    }

    const id = this.objectId
    undoStack.push({
      description: `Edit ground ${id}`,
      undo: () => gardenObjectState.updateObject(id, before),
      redo: () => gardenObjectState.updateObject(id, after),
    })

    debug.log('GroundShapeAdapter', `Committed edit for ${id}`)
    this.snapshot = null
  }

  cancel(): void {
    if (!this.snapshot) return
    gardenObjectState.updateObject(this.objectId, this.snapshot)
    debug.log('GroundShapeAdapter', `Cancelled edit for ${this.objectId}`)
    this.snapshot = null
  }
}
